import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ParsedSubscriptionStatus, SubscriptionStatusResponse } from '../models/membership-tier.model';
import { DEFAULT_SUBSCRIPTION_STATUS, parseSubscriptionStatus } from '../utils/membership-tier-utils';
import { WebSocketService } from './websocket.service';

/**
 * Web-side Core/Pro tier signal.
 *
 * Named `MembershipTierService` (not `MembershipService`) to avoid colliding
 * with the pre-existing `MembershipService` (`membership.service.ts`), which
 * manages org-*role* membership (Owner/ChiefSupervisor/add-remove-members) —
 * an unrelated concept. Mirrors the same rename the Hub-side resolver went
 * through for the identical reason (`membership-tier-service.ts`).
 *
 * Read-only signal store, matching `CustomClaimService`/`AuthService`
 * conventions (not NgRx). Tier is never derived here — this is a thin cache
 * of whatever `GET /payments/subscriptions/status` last returned, which
 * itself only ever reads through the Hub's single authoritative resolver.
 *
 * Reference: agent-os/specs/2026-06-29-core-pro-membership/spec.md (FR-3.1)
 */
@Injectable({ providedIn: 'root' })
export class MembershipTierService implements OnDestroy {
  private http = inject(HttpClient);
  private webSocketService = inject(WebSocketService);
  private apiUrl = environment.apiUrl;

  private stateSignal = signal<ParsedSubscriptionStatus>(DEFAULT_SUBSCRIPTION_STATUS);

  // Matches every other WebSocketService consumer in this codebase
  // (WorkOrderRealtimeService, TicketRealtimeService, FaaReportService,
  // AgremoService, etc.) — all pipe their long-lived subscriptions through
  // takeUntil(destroy$) and tear down in ngOnDestroy, even though a
  // providedIn: 'root' service is never actually destroyed in production.
  // Kept for consistency with that convention (and for test-harness
  // teardown hygiene) rather than because it fires in prod.
  private destroy$ = new Subject<void>();

  constructor() {
    this.listenForTierChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * ADN-2327 — re-fetch on a server-pushed "membership.tier.changed" ping,
   * closing the staleness gap for subscription changes made OUTSIDE ADN's
   * own Checkout/Portal-return flows (e.g. a subscription cancelled
   * directly in the Stripe Dashboard), which previously left an already-
   * open session's cached tier stale until the next app-load.
   *
   * `WebSocketService.on()` checks for a live socket ONCE at subscribe
   * time and silently no-ops if the socket doesn't exist yet — this
   * service is a root singleton that can construct before login/connect,
   * so subscribing to `.on()` directly here would attach nothing and never
   * retry. Other WS-consuming services in this codebase solve the same
   * problem with a manual `isSubscribed` boolean + re-call on every
   * `connectionState$` transition — but that never tears down a stale
   * `.on()` listener from a PRIOR connection cycle, only relying on the
   * old socket object being discarded. `switchMap` here instead
   * re-subscribes to `.on()` fresh every time the socket (re)reaches a
   * connected state — covering first connect, reconnects after a drop,
   * and re-login — while automatically unsubscribing the previous inner
   * subscription each time, so a reconnect can't stack duplicate
   * listeners.
   */
  private listenForTierChanges(): void {
    this.webSocketService.connectionState$
      .pipe(
        filter((state) => state === 'connected' || state === 'authenticated'),
        switchMap(() => this.webSocketService.on('membership.tier.changed')),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        void this.fetchStatus();
      });
  }

  // Tracks the most recent fetchStatus() call so awaitReady() can wait on
  // the SAME in-flight fetch AuthService kicked off at login, rather than
  // triggering a redundant one — see awaitReady()'s own docstring.
  private fetchPromise: Promise<void> | null = null;

  // Bumped on every fetchStatus() call and on reset(). A fetch checks this
  // before writing its result — if it no longer matches the generation it
  // was issued under, a newer fetch (or a logout) has superseded it, and
  // its write is discarded instead of clobbering fresher (or intentionally
  // reset) state.
  private fetchGeneration = 0;

  // True once a fetchStatus() call under the CURRENT generation has
  // settled at least once. Before that, isPro()/tier() etc. only hold
  // DEFAULT_SUBSCRIPTION_STATUS's placeholder Core value — callers that
  // render a Pro-only affordance (e.g. the sidebar's lock icon) should
  // gate on this so a Pro org doesn't flash as "locked" during the
  // in-flight window right after login.
  private hasFetchedSignal = signal(false);
  readonly hasFetched = computed(() => this.hasFetchedSignal());

  // True when the MOST RECENT completed fetch hit a real error (network/HTTP
  // failure), as opposed to a successful call that simply resolved to Core.
  // fetchStatus() itself never throws (see below), so callers that need to
  // distinguish "confirmed Core" from "couldn't confirm at all" — e.g. the
  // Checkout-return page, where the two read very differently to a user who
  // just paid — read this instead of relying on a try/catch around
  // fetchStatus() (Copilot catch on PR #108).
  private lastFetchFailedSignal = signal(false);
  readonly lastFetchFailed = computed(() => this.lastFetchFailedSignal());

  readonly tier = computed(() => this.stateSignal().tier);
  readonly isPro = computed(() => this.stateSignal().isPro);
  readonly status = computed(() => this.stateSignal().status);
  readonly renewalDate = computed(() => this.stateSignal().renewalDate);
  readonly cancelAtPeriodEnd = computed(() => this.stateSignal().cancelAtPeriodEnd);
  readonly priceCents = computed(() => this.stateSignal().priceCents);

  /**
   * Fetch and cache the current org's tier. Called on app-load bootstrap
   * (alongside where `AuthService` loads organization context) and again
   * after a Checkout return (Task Groups 8/9). Never throws — a failure
   * fails closed to Core rather than leaving a stale or optimistic state,
   * matching `AgremoService.fetchStatus`'s same fail-safe pattern.
   */
  async fetchStatus(): Promise<void> {
    const generation = ++this.fetchGeneration;
    const promise = this.doFetchStatus(generation);
    this.fetchPromise = promise;
    return promise;
  }

  private async doFetchStatus(generation: number): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<SubscriptionStatusResponse>(`${this.apiUrl}/payments/subscriptions/status`)
      );
      // Superseded by a newer fetchStatus() call or a reset() (logout)
      // while this request was in flight — discard rather than clobber
      // whatever state is now current.
      if (generation !== this.fetchGeneration) return;
      if (response?.success && response.data) {
        this.stateSignal.set(parseSubscriptionStatus(response.data));
        this.lastFetchFailedSignal.set(false);
      } else {
        // An HTTP-level 200 with a malformed/unsuccessful body is just as
        // much a "couldn't confirm" case as a network error — a caller like
        // the Checkout-return page needs to tell this apart from a genuine
        // confirmed-Core result, so it counts as a failure too (Copilot
        // catch on PR #108).
        console.warn('[MembershipTierService] status fetch returned no data, defaulting to Core');
        this.stateSignal.set(DEFAULT_SUBSCRIPTION_STATUS);
        this.lastFetchFailedSignal.set(true);
      }
      this.hasFetchedSignal.set(true);
    } catch (error) {
      if (generation !== this.fetchGeneration) return;
      console.error('[MembershipTierService] Failed to fetch subscription status, defaulting to Core', error);
      this.stateSignal.set(DEFAULT_SUBSCRIPTION_STATUS);
      this.lastFetchFailedSignal.set(true);
      this.hasFetchedSignal.set(true);
    } finally {
      // Only clear if this generation is still current — a superseded
      // fetch settling after a newer one has already replaced fetchPromise
      // must not null out the CURRENT in-flight fetch's promise out from
      // under awaitReady().
      if (generation === this.fetchGeneration) {
        this.fetchPromise = null;
      }
    }
  }

  /**
   * Resolves once the most recently started `fetchStatus()` has settled —
   * a no-op if none is in flight (or none has ever run, e.g. a user with no
   * org, in which case `isPro()`'s DEFAULT_SUBSCRIPTION_STATUS Core value is
   * already correct). `AuthService` fires `fetchStatus()` without awaiting
   * it (deliberately, to keep it off the login critical path — PR #106),
   * which otherwise leaves a window where `isPro()` reads the pre-fetch
   * default. `MembershipTierGuard` awaits this before reading `isPro()` so
   * a Pro org's very first navigation after login can't be misread as Core.
   */
  async awaitReady(): Promise<void> {
    if (this.fetchPromise) {
      await this.fetchPromise;
    }
  }

  /**
   * Clear cached tier state on logout — otherwise a Pro org's status could
   * briefly leak into a subsequent login as a different (possibly Core) org
   * before the next `fetchStatus()` resolves.
   */
  reset(): void {
    this.fetchGeneration++; // invalidate any in-flight fetch's pending write
    this.stateSignal.set(DEFAULT_SUBSCRIPTION_STATUS);
    this.fetchPromise = null;
    this.hasFetchedSignal.set(false);
    this.lastFetchFailedSignal.set(false);
  }
}
